import { HandleType, CalculateHelper } from './calculateHelper';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { Line2d } from '@/editor/math/Line2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { EPointOnSegmentType } from '@/editor/types';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { RelativeWallInfo, RelativeWallInfoItem } from '@/editor/data/wall/Wall';
import { ISimpleUniformShapeWallInfo, IWallInfo, IWallShape } from '@/editor/types/wallShape';
import { getBaseShape, getWallDrawShape } from '.';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { ERelationToSeg, IShapeSeg2d, IShapePoint2d } from '@/editor/types/shape';
import { Circle2d } from '@/editor/math/circle2d';
import { segRelations } from '@/editor/math/shape/relation';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import { isArc } from '../arc';

interface IWallStore {
  getUniformShapeWallInfos(): ISimpleUniformShapeWallInfo[]; // 获取基础的墙体信息
  updateWallShape(wallId: string, drawInfo: IWallShape): void;
}

export class WallShapeCalculate {
  private _store: IWallStore;
  private _mainWallInfos: IWallInfo[] = [];
  private _calculateHelper: CalculateHelper;
  private _wallInfos: IWallInfo[] = [];
  private _wallIndexMap: Map<string, number> = new Map();

  constructor(store: IWallStore) {
    this._store = store;
    this._calculateHelper = new CalculateHelper();
  }

  // skipCalculate 为 true 不计算连接关系
  getConnectedWallInfo(mainWallId: string) {
    const wallInfos = this._wallInfos;
    if (!wallInfos.length) return;
    const mainWallData = this._wallInfos.find((wallInfo) => wallInfo.id === mainWallId);
    if (!mainWallData) return;

    const { start, end, center, clockwise } = mainWallData;
    // const mainSeg = new LineSeg2d(start, end);
    const mainSeg: IShapeSeg2d =
      center && clockwise !== undefined
        ? new ArcSeg2d({ start, end, center, clockwise: clockwise })
        : new LineSeg2d(start, end);
    const mainStartPoint = Point2dUtils.clone(start);
    const mainEndPoint = Point2dUtils.clone(end);

    const mainStartConnectToPoint: RelativeWallInfoItem[] = []; // 主墙体起始点与墙体端点相连的墙体
    let mainStartConnectToPointOrder: number = 0; // 主墙体所属顺序
    const mainEndConnectToPoint: RelativeWallInfoItem[] = []; // 主墙体终点与墙体端点相连的墙体
    let mainEndConnectToPointOrder: number = 0; // 主墙体所属顺序

    const mainStartOnWallInternal: RelativeWallInfoItem[] = []; // 主墙体起始点在墙体上的墙体(不包含端点处的连接)
    const mainEndOnWallInternal: RelativeWallInfoItem[] = []; // 主墙体终点在墙体上的墙体(不包含端点处的连接)

    const startPointOnMainWallInternal: RelativeWallInfoItem[] = []; // 端点在主墙体内的墙体(不包含端点处的连接)
    const endPointOnMainWallInternal: RelativeWallInfoItem[] = []; // 端点在主墙体内的墙体(不包含端点处的连接)

    const collinear: RelativeWallInfoItem[] = []; // 与主墙体端点共线的墙体,不包含mainStartOnWall,mainEndOnWall,mainStartConnectToPoint,mainEndConnectToPoint中的墙体
    const intersectionInternal: RelativeWallInfoItem[] = []; // 与主墙体端点相交的墙体(不包含端点处的连接)
    const same: RelativeWallInfoItem[] = []; // 与主墙体端点重合的墙体

    for (const wd of wallInfos) {
      let relations = segRelations(mainSeg, wd.seg);
      if (!Array.isArray(relations)) relations = [relations];
      relations.forEach((relation) => {
        switch (relation) {
          case ERelationToSeg.Same:
            same.push({
              ...wd,
              connectPoint: null,
              connectType: EPointOnSegmentType.Empty
            });

            mainStartConnectToPointOrder = mainStartConnectToPoint.length;
            mainEndConnectToPointOrder = mainEndConnectToPoint.length;
            break;
          case ERelationToSeg.MainStartStartOverlap:
            mainStartConnectToPoint.push({
              ...wd,
              connectPoint: mainStartPoint,
              connectType: EPointOnSegmentType.StartPoint
            });
            break;
          case ERelationToSeg.MainStartEndOverlap:
            mainStartConnectToPoint.push({
              ...wd,
              connectPoint: mainStartPoint,
              connectType: EPointOnSegmentType.EndPoint
            });
            break;
          case ERelationToSeg.MainEndStartOverlap:
            mainEndConnectToPoint.push({
              ...wd,
              connectPoint: mainEndPoint,
              connectType: EPointOnSegmentType.StartPoint
            });
            break;
          case ERelationToSeg.MainEndEndOverlap:
            mainEndConnectToPoint.push({
              ...wd,
              connectPoint: mainEndPoint,
              connectType: EPointOnSegmentType.EndPoint
            });
            break;
          case ERelationToSeg.MainStartOnSegInternal:
            mainStartOnWallInternal.push({
              ...wd,
              connectPoint: mainStartPoint,
              connectType: EPointOnSegmentType.Between
            });
            break;
          case ERelationToSeg.MainEndOnSegInternal:
            mainEndOnWallInternal.push({
              ...wd,
              connectPoint: mainEndPoint,
              connectType: EPointOnSegmentType.Between
            });
            break;
          case ERelationToSeg.MainSegContainStartInternal:
            startPointOnMainWallInternal.push({
              ...wd,
              connectPoint: wd.start,
              connectType: EPointOnSegmentType.StartPoint
            });
            break;
          case ERelationToSeg.MainSegContainEndInternal:
            endPointOnMainWallInternal.push({
              ...wd,
              connectPoint: wd.end,
              connectType: EPointOnSegmentType.EndPoint
            });
            break;
          case ERelationToSeg.Overlap:
            collinear.push({
              ...wd,
              connectPoint: null,
              connectType: EPointOnSegmentType.Empty
            });
            break;
          case ERelationToSeg.IntersectInternal:
            {
              const ps = intersectCommonShape(mainSeg, wd.seg);
              const connectPoint = ps ? ps[0] : null; // TODO: 待修复，处理多交点情况
              intersectionInternal.push({
                ...wd,
                connectPoint,
                connectType: EPointOnSegmentType.Between
              });
            }
            break;
        }
      });
    }

    return {
      collinear,
      mainStartConnectToPoint,
      mainStartConnectToPointOrder,
      mainEndConnectToPoint,
      mainEndConnectToPointOrder,
      mainStartOnWallInternal,
      mainEndOnWallInternal,
      startPointOnMainWallInternal,
      endPointOnMainWallInternal,
      intersectionInternal,
      same
    };
  }

  // 一次性计算所有墙体关系，提高计算效率
  getConnectedWallInfos() {
    const wallInfos = this._wallInfos;
    if (!wallInfos.length) return [];

    const relativeWallInfos = wallInfos.map((wallInfo): RelativeWallInfo & { id: string } => {
      return {
        id: wallInfo.id,
        mainStartConnectToPoint: [], // 主墙体起始点与墙体端点相连的墙体
        mainStartConnectToPointOrder: 0, // 主墙体所属顺序
        mainEndConnectToPoint: [], // 主墙体终点与墙体端点相连的墙体
        mainEndConnectToPointOrder: 0, // 主墙体所属顺序

        mainStartOnWallInternal: [], // 主墙体起始点在墙体上的墙体(不包含端点处的连接)
        mainEndOnWallInternal: [], // 主墙体终点在墙体上的墙体(不包含端点处的连接)
        startPointOnMainWallInternal: [], // 端点在主墙体内的墙体(不包含端点处的连接)
        endPointOnMainWallInternal: [], // 端点在主墙体内的墙体(不包含端点处的连接)
        intersectionInternal: [], // 与主墙体端点相交的墙体(不包含端点处的连接)

        collinear: [], // 与主墙体端点共线的墙体,不包含mainStartOnWall,mainEndOnWall,mainStartConnectToPoint,mainEndConnectToPoint中的墙体
        same: [] // 与主墙体端点重合的墙体
      };
    });

    for (let i = 0; i < wallInfos.length; i++) {
      const mainWallInfo = wallInfos[i];
      const mainRelativeWallInfo = relativeWallInfos[i];
      mainRelativeWallInfo.same.push({
        ...mainWallInfo,
        connectPoint: null,
        connectType: EPointOnSegmentType.Empty
      });
      mainRelativeWallInfo.mainStartConnectToPointOrder = mainRelativeWallInfo.mainStartConnectToPoint.length;
      mainRelativeWallInfo.mainEndConnectToPointOrder = mainRelativeWallInfo.mainEndConnectToPoint.length;

      for (let j = i + 1; j < relativeWallInfos.length; j++) {
        const subWallInfo = wallInfos[j];
        const subRelativeWallInfo = relativeWallInfos[j];
        let relations = segRelations(mainWallInfo.seg, subWallInfo.seg);
        if (!Array.isArray(relations)) relations = [relations];

        relations.forEach((relation) => {
          switch (relation) {
            case ERelationToSeg.Same:
              // 目前墙体不支持重合，所以不处理
              break;
            case ERelationToSeg.MainStartStartOverlap:
              mainRelativeWallInfo.mainStartConnectToPoint.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.StartPoint
              });
              subRelativeWallInfo.mainStartConnectToPoint.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.StartPoint
              });
              break;
            case ERelationToSeg.MainStartEndOverlap:
              mainRelativeWallInfo.mainStartConnectToPoint.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.EndPoint
              });
              subRelativeWallInfo.mainEndConnectToPoint.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.StartPoint
              });
              break;
            case ERelationToSeg.MainEndStartOverlap:
              mainRelativeWallInfo.mainEndConnectToPoint.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.StartPoint
              });
              subRelativeWallInfo.mainStartConnectToPoint.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              break;
            case ERelationToSeg.MainEndEndOverlap:
              mainRelativeWallInfo.mainEndConnectToPoint.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              subRelativeWallInfo.mainEndConnectToPoint.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              break;
            case ERelationToSeg.MainStartOnSegInternal:
              mainRelativeWallInfo.mainStartOnWallInternal.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.Between
              });
              subRelativeWallInfo.startPointOnMainWallInternal.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.start,
                connectType: EPointOnSegmentType.StartPoint
              });
              break;
            case ERelationToSeg.MainEndOnSegInternal:
              mainRelativeWallInfo.mainEndOnWallInternal.push({
                ...subWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.Between
              });
              subRelativeWallInfo.startPointOnMainWallInternal.push({
                ...mainWallInfo,
                connectPoint: mainWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              break;
            case ERelationToSeg.MainSegContainStartInternal:
              mainRelativeWallInfo.startPointOnMainWallInternal.push({
                ...subWallInfo,
                connectPoint: subWallInfo.start,
                connectType: EPointOnSegmentType.StartPoint
              });
              subRelativeWallInfo.mainStartOnWallInternal.push({
                ...mainWallInfo,
                connectPoint: subWallInfo.start,
                connectType: EPointOnSegmentType.Between
              });
              break;
            case ERelationToSeg.MainSegContainEndInternal:
              mainRelativeWallInfo.endPointOnMainWallInternal.push({
                ...subWallInfo,
                connectPoint: subWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              subRelativeWallInfo.mainEndOnWallInternal.push({
                ...mainWallInfo,
                connectPoint: subWallInfo.end,
                connectType: EPointOnSegmentType.EndPoint
              });
              break;
            case ERelationToSeg.Overlap:
              mainRelativeWallInfo.collinear.push({
                ...subWallInfo,
                connectPoint: null,
                connectType: EPointOnSegmentType.Empty
              });
              subRelativeWallInfo.collinear.push({
                ...mainWallInfo,
                connectPoint: null,
                connectType: EPointOnSegmentType.Empty
              });
              break;
            case ERelationToSeg.IntersectInternal:
              {
                const ps = intersectCommonShape(mainWallInfo.seg, subWallInfo.seg);
                const connectPoint = ps ? ps[0] : null; // TODO: 待修复，处理多交点情况

                mainRelativeWallInfo.intersectionInternal.push({
                  ...subWallInfo,
                  connectPoint,
                  connectType: EPointOnSegmentType.Between
                });
                subRelativeWallInfo.intersectionInternal.push({
                  ...mainWallInfo,
                  connectPoint,
                  connectType: EPointOnSegmentType.Between
                });
              }
              break;
          }
        });
      }
    }

    return relativeWallInfos;
  }

  private convertToCompleteWallInfo(simpleWallInfos: ISimpleUniformShapeWallInfo[]) {
    return simpleWallInfos.map((item) => {
      if (!item.drawShape.length) {
        const { baseShape, startShape, endShape } = getBaseShape(item);
        // 直接修改了，可能有风险
        item.drawShape = baseShape;
        item.startShape = startShape;
        item.endShape = endShape;
      }

      // 弧墙
      if (isArc(item)) {
        const arc = new ArcSeg2d({ start: item.start, end: item.end, center: item.center, clockwise: item.clockwise });
        const leftRadiusOffset = (item.thickness / 2) * (arc.clockwise ? 1 : -1);
        const leftArc = arc.getOffsetArc(leftRadiusOffset);
        const rightArc = arc.getOffsetArc(-leftRadiusOffset);

        const circle = new Circle2d({ center: arc.center, radius: arc.radius });
        const leftCircle = new Circle2d({ center: arc.center, radius: leftArc.radius });
        const rightCircle = new Circle2d({ center: arc.center, radius: rightArc.radius });

        return {
          ...item,
          seg: arc,
          leftSeg: leftArc,
          rightSeg: rightArc,

          shape: circle,
          leftShape: leftCircle,
          rightShape: rightCircle
        };
      }

      const seg = new LineSeg2d(item.start, item.end);
      const { leftCurve, rightCurve } = Curve2dUtils.offsetCurves(seg, item.thickness / 2);

      const line = new Line2d({ points: { p1: item.start, p2: item.end } });
      const leftLine = new Line2d({ points: { p1: leftCurve.start, p2: leftCurve.end } });
      const rightLine = new Line2d({ points: { p1: rightCurve.start, p2: rightCurve.end } });
      return {
        ...item,

        seg: seg,
        leftSeg: leftCurve,
        rightSeg: rightCurve,

        shape: line,
        leftShape: leftLine,
        rightShape: rightLine
      };
    });
  }

  /**
   * @param includeSkipWall 是否包含要跳过墙体（添加该参数是为了一些地方获取完整关联关系，后面再优化）
   * @returns
   */
  updateWallInfos(excludeSkipWall: boolean = true) {
    this._wallInfos = this.convertToCompleteWallInfo(
      this._store.getUniformShapeWallInfos().filter((wallInfo) => !excludeSkipWall || !wallInfo.skipCalculate)
    );

    this._wallIndexMap.clear();
    this._wallInfos.forEach((wallInfo, index) => {
      this._wallIndexMap.set(wallInfo.id, index);
    });

    return this;
  }

  setWalls(ids: Set<string>) {
    if (!ids.size || !this._wallInfos.length) return this;

    this._mainWallInfos = [];

    this._wallInfos.forEach((wallInfo) => {
      if (ids.has(wallInfo.id)) this._mainWallInfos.push(wallInfo);
    });

    return this;
  }

  compute() {
    this._calculateHelper.clear();

    if (!this._mainWallInfos.length || !this._wallInfos.length) return this;

    this._mainWallInfos.forEach((wallInfo) => {
      this.addHandleWall(wallInfo.id);
    });

    this._calculateHelper.startChainCalculate();

    return this;
  }

  updateShapes() {
    // 更新绘制信息
    const modifyList = this._calculateHelper.result;

    // 记录各端原始(计算前)封口，用于判断哪一半是被斜接调整过的
    const rawCaps = new Map<string, IShapePoint2d[]>();
    const changed = new Set<IWallInfo>();
    for (const item of modifyList) {
      const w = this._wallInfos.find((x) => x.id === item.wallId);
      if (!w) continue;
      rawCaps.set(`${item.wallId}|${item.type}`, item.type === 'start' ? w.startShape : w.endShape);
      if (item.type === 'start') w.startShape = [...item.points];
      else w.endShape = [...item.points];
      changed.add(w);
    }

    // 同一圆的两半墙在共享端点处的封口本应完全一致；若因斜接非对称差了几 mm，会让房间闭环找不到。
    // 将被斜接调整过的那一半的封口复制给保留原始(raw)的那一半，使两半严格重合(不放大全局容差，不误伤其它墙)。
    this._unifyCircleHalfCaps([...changed], rawCaps);

    for (const w of changed) {
      this._store.updateWallShape(w.id, {
        startShape: w.startShape,
        endShape: w.endShape,
        drawShape: getWallDrawShape(
          { start: w.start, end: w.end, center: w.center, clockwise: w.clockwise },
          w.startShape,
          w.endShape
        )
      });
    }
  }

  // 统一「同一圆两半墙」在共享端点处的封口
  private _unifyCircleHalfCaps(walls: IWallInfo[], rawCaps: Map<string, IShapePoint2d[]>) {
    const near = (a: IShapePoint2d, b: IShapePoint2d, tol = 1) => Math.hypot(a.x - b.x, a.y - b.y) <= tol;
    const capAt = (w: IWallInfo, p: IShapePoint2d): { type: 'start' | 'end'; cap: IShapePoint2d[] } =>
      Math.hypot(w.start.x - p.x, w.start.y - p.y) <= Math.hypot(w.end.x - p.x, w.end.y - p.y)
        ? { type: 'start', cap: w.startShape }
        : { type: 'end', cap: w.endShape };
    // 封口是否相对原始被改动过(斜接)。按最近点匹配比较(计算后左右顺序可能翻转，不能按下标比)
    const isMitered = (w: IWallInfo, type: 'start' | 'end', cap: IShapePoint2d[]) => {
      const raw = rawCaps.get(`${w.id}|${type}`);
      if (!raw || raw.length !== cap.length) return true;
      return cap.some((p) => Math.min(...raw.map((r) => Math.hypot(p.x - r.x, p.y - r.y))) > 0.01);
    };
    // 把 src 封口复制给 dst(按最近点配对，避免左右顺序差异)。
    // 配对必须是一一对应：原先每个 dst 点各自独立取最近的 src 点，没有单射约束 ——
    // 当 src 那一半的封口在多墙接头处被拉出长斜接尖点时，dst 的两个点会同时匹配到 src 的同一个点，
    // 端盖被压成零长度(墙体轮廓塌成一条线、面积为 0、自相交，区域识别随之丢环)。
    // 改为在「同序 / 反序」两种一一对应中取总距离更小者(原意就是兜住左右顺序翻转)；点数不同则不复制。
    const copyCap = (src: IShapePoint2d[], dst: IShapePoint2d[]) => {
      if (src.length !== dst.length) return;
      const dist = (a: IShapePoint2d, b: IShapePoint2d) => Math.hypot(a.x - b.x, a.y - b.y);
      let asIs = 0;
      let reversed = 0;
      for (let k = 0; k < dst.length; k++) {
        asIs += dist(dst[k], src[k]);
        reversed += dist(dst[k], src[src.length - 1 - k]);
      }
      const paired = reversed < asIs ? [...src].reverse() : src;
      dst.forEach((q, k) => {
        q.x = paired[k].x;
        q.y = paired[k].y;
      });
    };

    for (let i = 0; i < walls.length; i++) {
      for (let j = i + 1; j < walls.length; j++) {
        const wi = walls[i];
        const wj = walls[j];
        if (!(wi.seg instanceof ArcSeg2d) || !(wj.seg instanceof ArcSeg2d)) continue;
        if (!wi.center || !wj.center) continue;
        if (!near(wi.center, wj.center, 1)) continue;
        if (Math.abs(wi.seg.radius - wj.seg.radius) > 1) continue;
        // 同一圆。对两墙共享的端点统一封口
        for (const p of [wi.start, wi.end]) {
          if (!near(wj.start, p) && !near(wj.end, p)) continue; // 不是共享端点
          const ci = capAt(wi, p);
          const cj = capAt(wj, p);
          const mi = isMitered(wi, ci.type, ci.cap);
          const mj = isMitered(wj, cj.type, cj.cap);
          if (mi && !mj) copyCap(ci.cap, cj.cap);
          else if (mj && !mi) copyCap(cj.cap, ci.cap);
          // 两者都被斜接 / 都为原始：本就一致或无可靠依据，保持不动
        }
      }
    }
  }

  /**
   * 墙体在 _wallInfos 里的下标（即建立顺序，队列按它升序处理）。
   * 相连墙可能不在本次计算范围内（如被 skipCalculate 过滤掉），这类墙没有下标，按「最后处理」计。
   */
  private _wallIndexOf(wallId: string) {
    return this._wallIndexMap.get(wallId) ?? Infinity;
  }

  private addHandleWall(wallId: string) {
    // --- 添加完整的墙体数据 ---
    this._addWallInfoToCache(wallId);
    const wallInfo = this._calculateHelper.cacheWallMap.get(wallId);
    if (!wallInfo) return;

    const mainStartConnectToPoint = wallInfo.relativeWallInfos?.mainStartConnectToPoint || [];
    const mainEndConnectToPoint = wallInfo.relativeWallInfos?.mainEndConnectToPoint || [];

    const mainStartOnWallInternal = wallInfo.relativeWallInfos?.mainStartOnWallInternal || [];
    const mainEndOnWallInternal = wallInfo.relativeWallInfos?.mainEndOnWallInternal || [];
    const startPointOnMainWallInternal = wallInfo.relativeWallInfos?.startPointOnMainWallInternal || [];
    const endPointOnMainWallInternal = wallInfo.relativeWallInfos?.endPointOnMainWallInternal || [];

    // --- 起点处理 ---
    if (!mainStartOnWallInternal.length) {
      // --- 纯端点端点连接处理 ---
      this._calculateHelper.addToPointPointConnectedQueue({
        wallId,
        type: HandleType.Start,
        wallIndex: this._wallIndexOf(wallId)
      });
      mainStartConnectToPoint.forEach((item) => {
        this._addWallInfoToCache(item.id);
        this._calculateHelper.addToPointPointConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    } else {
      // --- 包含端点墙体连接情况的端点处理 ---
      this._calculateHelper.addToPointWallConnectedQueue({
        wallId,
        type: HandleType.Start,
        wallIndex: this._wallIndexOf(wallId)
      });
      mainStartOnWallInternal.forEach((item) => {
        // 不处理这种端点，但需要添加到缓存
        this._addWallInfoToCache(item.id);
      });
      mainStartConnectToPoint.forEach((item) => {
        this._addWallInfoToCache(item.id);
        this._calculateHelper.addToPointWallConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    }

    // --- 终点处理 ---
    if (!mainEndOnWallInternal.length) {
      this._calculateHelper.addToPointPointConnectedQueue({
        wallId,
        type: HandleType.End,
        wallIndex: this._wallIndexOf(wallId)
      });

      mainEndConnectToPoint.forEach((item) => {
        this._addWallInfoToCache(item.id);
        this._calculateHelper.addToPointPointConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    } else {
      this._calculateHelper.addToPointWallConnectedQueue({
        wallId,
        type: HandleType.End,
        wallIndex: this._wallIndexOf(wallId)
      });
      mainEndOnWallInternal.forEach((item) => {
        // 不处理这种端点，但需要添加到缓存
        this._addWallInfoToCache(item.id);
      });
      mainEndConnectToPoint.forEach((item) => {
        this._addWallInfoToCache(item.id);
        this._calculateHelper.addToPointWallConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    }

    // --- 其他墙体在主墙体上 ---
    if (startPointOnMainWallInternal.length) {
      startPointOnMainWallInternal.forEach((item) => {
        this._addWallInfoToCache(item.id);
        this._calculateHelper.addToPointWallConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    }
    if (endPointOnMainWallInternal.length) {
      endPointOnMainWallInternal.forEach((item) => {
        this._addWallInfoToCache(item.id);
        this._calculateHelper.addToPointWallConnectedQueue({
          wallId: item.id,
          type: item.connectType === EPointOnSegmentType.StartPoint ? HandleType.Start : HandleType.End,
          wallIndex: this._wallIndexOf(item.id)
        });
      });
    }
  }

  private _addWallInfoToCache(wallId: string) {
    if (this._calculateHelper.cacheWallMap.has(wallId)) return;
    const currentWallInfo = this._wallInfos.find((wallInfo) => wallInfo.id === wallId); // TODO: 待优化，列表查找耗时
    if (!currentWallInfo) return;

    const relativeWallInfos = this.getConnectedWallInfo(wallId);

    this._calculateHelper.addCacheWall(wallId, {
      ...currentWallInfo,
      relativeWallInfos
    });
  }
}
